import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { usePasswordValidation, type PasswordValidation } from '@/hooks/usePasswordValidation';
import { cn } from '@/lib/utils';

interface PasswordInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  showStrength?: boolean;
  error?: string;
  disabled?: boolean;
  autoComplete?: string;
}

export function PasswordInput({
  id,
  label,
  value,
  onChange,
  placeholder = 'Enter password',
  showStrength = false,
  error,
  disabled = false,
  autoComplete = 'new-password',
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const validation = usePasswordValidation(value);
  const errorId = `${id}-error`;

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        <Input
          id={id}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'pr-10',
            error && 'border-red-500 focus-visible:ring-red-500'
          )}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 rounded"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          aria-pressed={showPassword}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>

      {error && (
        <p id={errorId} className="text-sm text-red-500" role="alert" aria-live="polite">
          {error}
        </p>
      )}

      {showStrength && value.length > 0 && (
        <PasswordStrengthIndicator validation={validation} />
      )}
    </div>
  );
}

function PasswordStrengthIndicator({ validation }: { validation: PasswordValidation }) {
  const strengthColors = {
    weak: 'bg-red-500',
    fair: 'bg-orange-500',
    good: 'bg-yellow-500',
    strong: 'bg-green-500',
  };

  const strengthLabels = {
    weak: 'Weak',
    fair: 'Fair',
    good: 'Good',
    strong: 'Strong',
  };

  return (
    <div className="space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all duration-300',
              strengthColors[validation.strength]
            )}
            style={{ width: `${validation.strengthPercent}%` }}
          />
        </div>
        <span className={cn(
          'text-xs font-medium',
          validation.strength === 'weak' && 'text-red-500',
          validation.strength === 'fair' && 'text-orange-500',
          validation.strength === 'good' && 'text-yellow-600',
          validation.strength === 'strong' && 'text-green-500',
        )}>
          {strengthLabels[validation.strength]}
        </span>
      </div>

      {/* Requirements checklist */}
      <div className="grid grid-cols-2 gap-1 text-xs">
        <RequirementItem
          met={validation.requirements.minLength}
          label="8+ characters"
        />
        <RequirementItem
          met={validation.requirements.hasUppercase}
          label="Uppercase"
        />
        <RequirementItem
          met={validation.requirements.hasLowercase}
          label="Lowercase"
        />
        <RequirementItem
          met={validation.requirements.hasNumber}
          label="Number"
        />
        <RequirementItem
          met={validation.requirements.hasSpecialChar}
          label="Special char"
        />
      </div>
    </div>
  );
}

function RequirementItem({ met, label }: { met: boolean; label: string }) {
  return (
    <div className={cn(
      'flex items-center gap-1',
      met ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
    )}>
      {met ? (
        <Check className="h-3 w-3" />
      ) : (
        <X className="h-3 w-3" />
      )}
      <span>{label}</span>
    </div>
  );
}
